/**
 * Sprint 74 — Export RGPD 1-clic (droit à la portabilité art. 20 RGPD).
 * Disponible pour TOUS les plans (obligation légale, pas de gating).
 */
import { useEffect, useState } from "react";
import api, { API, apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RgpdExportCard() {
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/rgpd/export/preview")
       .then((r) => setPreview(r.data))
       .catch((e) => toast.error(apiError(e)));
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const resp = await fetch(`${API}/rgpd/export`, { credentials: "include" });
      if (!resp.ok) throw new Error("Export échoué");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clarix_rgpd_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export RGPD téléchargé");
    } catch (e) {
      toast.error(e.message || "Erreur téléchargement");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card data-testid="rgpd-export-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          Export RGPD (droit à la portabilité)
        </CardTitle>
        <p className="text-sm text-slate-500">
          Téléchargez l'intégralité des données de votre organisation en un ZIP
          conforme à l'article 20 du RGPD. Fichiers JSON UTF-8, un par collection.
          Les secrets (mots de passe, tokens) sont automatiquement masqués.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {preview && (
          <div className="border rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">Aperçu du contenu</p>
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                {preview.total_docs} documents
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-56 overflow-y-auto text-xs">
              {Object.entries(preview.collections)
                .filter(([, n]) => n > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([coll, n]) => (
                  <div key={coll} className="flex justify-between px-2 py-1 bg-white rounded"
                        data-testid={`preview-${coll}`}>
                    <span className="text-slate-600 truncate">{coll}</span>
                    <span className="font-mono text-slate-500 ml-2">{n}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={download} disabled={downloading}
                   data-testid="download-rgpd-btn">
            {downloading
              ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</>)
              : (<><Download className="w-4 h-4 mr-2" />Télécharger l'export ZIP</>)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
